import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockGoodsReceiptService } from '@/services/mock/MockGoodsReceiptService';
import { GoodsReceiptRecord, QualityInspectionStatus } from '@/types/domain/grn-management';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Save, Truck, ShieldCheck, Warehouse, FileText } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const GrnEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [grn, setGrn] = useState<GoodsReceiptRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form fields
  const [storageLocation, setStorageLocation] = useState('');
  const [unloadingDock, setUnloadingDock] = useState('');
  const [deliveryChallanNumber, setDeliveryChallanNumber] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [qualityStatus, setQualityStatus] = useState<QualityInspectionStatus>('PASSED');
  const [inspectorName, setInspectorName] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');

  useEffect(() => {
    const loadGrn = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const data = await mockGoodsReceiptService.getGoodsReceiptById(id);
        if (!data) {
          navigate(ROUTES.GRN.LIST);
          return;
        }
        setGrn(data);
        setStorageLocation(data.storageLocation || 'SLOC-101');
        setUnloadingDock(data.unloadingDock || 'Dock-1A');
        setDeliveryChallanNumber(data.deliveryChallanNumber || '');
        setCarrierName(data.carrierName || '');
        setTrackingNumber(data.trackingNumber || '');
        setQualityStatus(data.qualityStatus || 'PASSED');
        setInspectorName(data.inspectorName || '');
        setRejectionNotes(data.rejectionNotes || '');
      } finally {
        setIsLoading(false);
      }
    };

    loadGrn();
  }, [id, navigate]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !grn) return;

    try {
      setIsSaving(true);
      await mockGoodsReceiptService.updateGoodsReceipt(id, {
        storageLocation,
        unloadingDock,
        deliveryChallanNumber,
        carrierName,
        trackingNumber,
        qualityStatus,
        inspectorName,
        rejectionNotes,
      });
      navigate(ROUTES.GRN.DETAILS(id));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update goods receipt note.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Goods Receipt Editor...
      </div>
    );
  }

  if (!grn) {
    return (
      <div className="p-16 text-center space-y-4 font-poppins">
        <h2 className="text-lg font-bold text-foreground">Goods Receipt Note Not Found</h2>
        <Button size="sm" onClick={() => navigate(ROUTES.GRN.LIST)} className="font-poppins cursor-pointer">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Goods Receipts
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 font-poppins max-w-5xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.GRN.DETAILS(id!))}
            className="rounded-xl border-border hover:bg-muted font-poppins cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Cancel
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-primary px-2.5 py-0.5 bg-primary/10 rounded-lg border border-primary/20">
                {grn.grnNumber}
              </span>
              <h1 className="text-xl font-extrabold text-foreground tracking-tight font-poppins">
                Edit Goods Receipt Note (GRN)
              </h1>
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
              Supplier: <strong className="text-foreground">{grn.vendorName}</strong> • PO Reference: <strong className="text-primary font-mono">{grn.poNumber}</strong>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.GRN.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-grn-form"
            size="sm"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </div>

      <form id="edit-grn-form" onSubmit={handleSave} className="space-y-6">
        {/* Section 1: Dock & Warehouse Receipt Logistics */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <Warehouse className="h-4 w-4 text-primary" />
              Dock Receiving & Warehouse Storage
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Storage Location (SLoc) *
                </label>
                <select
                  value={storageLocation}
                  onChange={(e) => setStorageLocation(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="SLOC-101">SLOC-101 — Raw Materials General Bay</option>
                  <option value="SLOC-102">SLOC-102 — Finished Component Stores</option>
                  <option value="SLOC-103">SLOC-103 — Secure High-Value Cage</option>
                  <option value="SLOC-104">SLOC-104 — Quarantine Inspection Area</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Unloading Dock / Receiving Gate *
                </label>
                <select
                  value={unloadingDock}
                  onChange={(e) => setUnloadingDock(e.target.value)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="Dock-1A">Dock-1A (Heavy Commercial Bay)</option>
                  <option value="Dock-2B">Dock-2B (Express Couriers & Small Parcels)</option>
                  <option value="Dock-3C">Dock-3C (Temperature-Controlled Inwarding)</option>
                  <option value="Gate-4">Gate-4 Bulk Unloading Ramp</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5 text-primary" /> Delivery Challan (DC) # *
                </label>
                <input
                  type="text"
                  value={deliveryChallanNumber}
                  onChange={(e) => setDeliveryChallanNumber(e.target.value)}
                  required
                  placeholder="e.g. DC-990812"
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5 flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5 text-primary" /> Transporter / Logistics Provider
                </label>
                <input
                  type="text"
                  value={carrierName}
                  onChange={(e) => setCarrierName(e.target.value)}
                  placeholder="e.g. Blue Dart Express, VRL Logistics"
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Vehicle / Docket Tracking #
                </label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="e.g. MH-12-TR-4567, TRK-9908"
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Quality Inspection Baseline */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs font-poppins">
          <CardHeader className="p-4 border-b border-border/60">
            <CardTitle className="text-sm font-bold text-foreground flex items-center gap-2 font-poppins">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Quality Inspection & Acceptance Audit
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4 text-xs font-poppins">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Quality Audit Status *
                </label>
                <select
                  value={qualityStatus}
                  onChange={(e) => setQualityStatus(e.target.value as QualityInspectionStatus)}
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                >
                  <option value="PASSED">Passed — All Specifications Verified</option>
                  <option value="PARTIALLY_PASSED">Partially Passed — Minor Deviations Accepted</option>
                  <option value="ON_HOLD">On Hold — Lab / Dimensional Testing Pending</option>
                  <option value="FAILED">Failed — Technical Specification Breach</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-foreground mb-1.5">
                  Assigned QA Inspector Name *
                </label>
                <input
                  type="text"
                  value={inspectorName}
                  onChange={(e) => setInspectorName(e.target.value)}
                  required
                  placeholder="e.g. Ramesh Kulkarni"
                  className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                />
              </div>
            </div>

            <div>
              <label className="block font-bold text-foreground mb-1.5">
                Inspector Observations & Gate Pass Remarks
              </label>
              <textarea
                rows={3}
                value={rejectionNotes}
                onChange={(e) => setRejectionNotes(e.target.value)}
                placeholder="Enter any physical damage observations, test cert verification notes, or packaging defects..."
                className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-medium text-foreground focus:ring-2 focus:ring-primary font-poppins"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Controls */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.GRN.DETAILS(id!))}
            className="font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSaving}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-6 shadow-xs"
          >
            <Save className="h-4 w-4 mr-1.5" /> Save Changes
          </Button>
        </div>
      </form>
    </div>
  );
};
